import { ref } from 'vue'
import { defineStore } from 'pinia'
import type { NotificationRequiredResult } from '@nsftx/seven-client-sdk-integrator'
import type { RetailNotification } from '#src/modules/notifications/interfaces/RetailNotification'

interface PendingNotification {
  resolve: (result: NotificationRequiredResult) => void
}

interface PendingNotificationOptions {
  resolve: PendingNotification['resolve']
}

const CLOSE_ACTION = 'CLOSE'

export const useNotificationsStore = defineStore('retailNotificationsStore', () => {
  const notifications = ref<RetailNotification[]>([])
  const pendingNotifications = new Map<string, PendingNotification>()

  const removeNotificationById = (id: string): void => {
    notifications.value = notifications.value.filter((notification) => notification.id !== id)
  }

  const settlePending = (id: string, result: NotificationRequiredResult) => {
    const pending = pendingNotifications.get(id)
    if (!pending) return

    pendingNotifications.delete(id)
    pending.resolve(result)
  }

  const resolveNotification = (id: string, result: NotificationRequiredResult): void => {
    removeNotificationById(id)
    settlePending(id, result)
  }

  const closeNotificationById = (
    id: string,
    origin: NotificationRequiredResult['origin'] = 'programmatic'
  ): void => {
    removeNotificationById(id)
    settlePending(id, { action: CLOSE_ACTION, origin })
  }

  const showNotification = (
    notification: RetailNotification,
    pendingOptions?: PendingNotificationOptions
  ): void => {
    notifications.value.push(notification)

    if (!notification.id || !pendingOptions) return

    pendingNotifications.set(notification.id, {
      resolve: pendingOptions.resolve
    })
  }

  const syncNotifications = (nextNotifications: RetailNotification[]): void => {
    notifications.value = nextNotifications
  }

  const clearNotifications = (): void => {
    const ids = notifications.value.flatMap(({ id }) => (id ? [id] : []))
    notifications.value = []
    ids.forEach((id) => {
      settlePending(id, { action: CLOSE_ACTION, origin: 'programmatic' })
    })
  }

  return {
    notifications,
    showNotification,
    closeNotificationById,
    resolveNotification,
    syncNotifications,
    clearNotifications
  }
})
